import React from "react";
import { ThunkDispatch } from "redux-thunk";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Label,
  FormGroup,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import { RouteComponentProps, withRouter } from "react-router";
import {
  IAppState,
  LoadingDisplay,
  ErrorDisplay,
  ApiRequest,
  DateDisplay,
  appPaths,
  LoadingState
} from "../../../Common";
import { endpoints } from "../..";
import { Workflow } from "../../Models/Workflow";
import {
  IGetTopicWorkflowsArguments,
  IUnloadTopicWorkflowsArguments,
  ISetTopicWorkflowsLoadingArguments,
  getWorkflows,
  unloadWorkflows,
  setLoading,
  TopicWorkflowAction
} from "../../../Topic/Actions/TopicWorkflowActions";
import { WorkflowTemplateOption } from "../../Constants/workflowEnums";

type Props = {};

type StateProps = {
  workflows: Workflow[];
  workflowLoadingState: LoadingState;
  workflowErrorMessage: string;
  workflowErrorCode?: number;
};

type DispatchProps = {
  getWorkflows: (args: IGetTopicWorkflowsArguments) => void;
  unloadWorkflows: (args: IUnloadTopicWorkflowsArguments) => void;
  setLoading: (args: ISetTopicWorkflowsLoadingArguments) => void;
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  isModalOpen: boolean;
  workflowName: string;
  templateOption: WorkflowTemplateOption;
  isTemplateDropdownOpen: boolean;
  selectedTemplateWorkflow?: Workflow;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  workflows: state.topicWorkflow.workflows,
  workflowLoadingState: state.topicWorkflow.loadingState,
  workflowErrorMessage: state.topicWorkflow.errorMessage,
  workflowErrorCode: state.topicWorkflow.errorCode
});

function mapDispatchToProps(
  dispatch: ThunkDispatch<IAppState, {}, TopicWorkflowAction>
): DispatchProps {
  return {
    getWorkflows: (args: IGetTopicWorkflowsArguments) => dispatch(getWorkflows(args)),
    unloadWorkflows: (args: IUnloadTopicWorkflowsArguments) => dispatch(unloadWorkflows(args)),
    setLoading: (args: ISetTopicWorkflowsLoadingArguments) => dispatch(setLoading(args)),
    showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
  };
}

class CreateWorkflowModal extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps,
  State
> {
  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps) {
    super(props);

    this.state = {
      isModalOpen: false,
      workflowName: "",
      templateOption: WorkflowTemplateOption.Blank,
      isTemplateDropdownOpen: false
    };

    this.onNameChange = this.onNameChange.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onToggleDropdown = this.onToggleDropdown.bind(this);
    this.onClickWorkflowTemplateOption = this.onClickWorkflowTemplateOption.bind(this);
    this.onClickWorkflowDropdownOption = this.onClickWorkflowDropdownOption.bind(this);
    this.onCreate = this.onCreate.bind(this);
    this.onCancel = this.onCancel.bind(this);
  }

  componentDidMount = () => this.loadWorkflowsIfNeeded(this.props);

  componentDidUpdate = (prevProps: Props & StateProps) => this.loadWorkflowsIfNeeded(prevProps);

  onNameChange(event: React.FormEvent<HTMLInputElement>) {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.setState({ workflowName: target.value });
  }

  onToggle() {
    this.setState((prevState: State) => ({
      isModalOpen: !prevState.isModalOpen
    }));
  }

  onToggleDropdown() {
    this.setState((prevState: State) => ({
      isTemplateDropdownOpen: !prevState.isTemplateDropdownOpen
    }));
  }

  onClickWorkflowTemplateOption(selectedOption: WorkflowTemplateOption) {
    this.setState((prevState: State) => ({
      templateOption: selectedOption
    }));
  }

  onClickWorkflowDropdownOption(selectedTemplateWorkflow: Workflow) {
    this.setState((prevState: State) => ({
      selectedTemplateWorkflow
    }));
  }

  onCreate() {
    // post to server
    ApiRequest.fromPath(endpoints.createTemplate())
      .withAuthHeader()
      .withJson({
        name: this.state.workflowName,
        templateOption: this.state.templateOption,
        otherWorkflowCode: this.state.selectedTemplateWorkflow
          ? this.state.selectedTemplateWorkflow.code
          : undefined
      })
      .post()
      .then(response => response.json())
      .then(json => {
        if (json.status === undefined) {
          // this was successful
          this.props.history.push(appPaths.workflows.edit.replace(":code", json.payload));
        } else {
          // this failed
          this.props.showSnack("CREATE_WORKFLOW", {
            label: `Failed to create workflow: ${json.error.message}`,
            timeout: 2000
          });
        }
      });
  }

  onCancel() {
    // just close the modal
    this.onToggle();
  }

  loadWorkflowsIfNeeded(prevProps: Props & StateProps) {
    const { isLoaded, isLoading, errored } = this.props.workflowLoadingState;

    if (!isLoaded && !isLoading && !errored) {
      this.reload();
    }
  }

  // dispatch the actions to load the workflows
  reload() {
    this.props.setLoading({});
    this.props.getWorkflows({});
  }

  render() {
    const isCreateDisabled =
      this.state.workflowName.length < 3 ||
      (this.state.templateOption === WorkflowTemplateOption.UseAnotherWorkflow &&
        !this.state.selectedTemplateWorkflow);

    return (
      <>
        <Button color="primary" onClick={this.onToggle}>
          Create New
        </Button>
        <Modal isOpen={this.state.isModalOpen} toggle={this.onToggle}>
          <ModalHeader toggle={this.onToggle}>Create New Workflow Template</ModalHeader>
          <ModalBody>
            <FormGroup>
              <legend>Workflow Template Name</legend>
              <input
                className="form-control"
                placeholder="Enter Workflow Template Name"
                value={this.state.workflowName}
                onChange={this.onNameChange}
              />
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Create From...</legend>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="createWorkflowTemplateRadio"
                    checked={this.state.templateOption === WorkflowTemplateOption.Blank}
                    onChange={() =>
                      this.onClickWorkflowTemplateOption(WorkflowTemplateOption.Blank)
                    }
                  />{" "}
                  Blank Workflow Template
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="createWorkflowTemplateRadio"
                    checked={
                      this.state.templateOption === WorkflowTemplateOption.UseAnotherWorkflow
                    }
                    onChange={() =>
                      this.onClickWorkflowTemplateOption(WorkflowTemplateOption.UseAnotherWorkflow)
                    }
                  />{" "}
                  Use Another Workflow Template
                </Label>
              </FormGroup>
            </FormGroup>
            {this.state.templateOption === WorkflowTemplateOption.UseAnotherWorkflow && (
              <FormGroup>
                <legend>Select Workflow Template to Use as Base</legend>
                {this.props.workflowLoadingState.isLoading && <LoadingDisplay />}
                {this.props.workflowLoadingState.errored && (
                  <ErrorDisplay
                    errorCode={this.props.workflowErrorCode}
                    errorDescription={this.props.workflowErrorMessage}
                    errorResult="Could not load workflow base options"
                  />
                )}
                {!this.props.workflowLoadingState.isLoading &&
                  !this.props.workflowLoadingState.errored &&
                  this.props.workflowLoadingState.isLoaded && (
                    <Dropdown
                      isOpen={this.state.isTemplateDropdownOpen}
                      toggle={this.onToggleDropdown}
                    >
                      <DropdownToggle caret>
                        {!!this.state.selectedTemplateWorkflow &&
                          this.state.selectedTemplateWorkflow.name}
                        {!this.state.selectedTemplateWorkflow && (
                          <span>Please select a workflow</span>
                        )}
                      </DropdownToggle>
                      <DropdownMenu>
                        {this.props.workflows.map((el: Workflow) => {
                          return (
                            <DropdownItem
                              key={el.code}
                              active={
                                !!this.state.selectedTemplateWorkflow &&
                                el.code === this.state.selectedTemplateWorkflow.code
                              }
                              onClick={() => this.onClickWorkflowDropdownOption(el)}
                            >
                              {el.name}
                            </DropdownItem>
                          );
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  )}
              </FormGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.onCreate} disabled={isCreateDisabled}>
              Create
            </Button>{" "}
            <Button color="secondary" onClick={this.onCancel}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(CreateWorkflowModal));
